import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "./utils";
import { translateUi } from "@i18n/config";

type DialogContextValue = { open: boolean; setOpen: (next: boolean) => void };
const DialogContext = React.createContext<DialogContextValue | null>(null);

type DialogProps = React.PropsWithChildren<{
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  maxWidthClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  placement?: "center" | "bottom";
}>;

function getFocusableElements(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      [
        "a[href]",
        "button:not([disabled])",
        'input:not([disabled]):not([type="hidden"])',
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(","),
    ),
  ).filter((element) => element.offsetParent !== null);
}

export const Dialog = ({
  open,
  onOpenChange,
  title,
  description,
  footer,
  maxWidthClassName,
  contentClassName,
  bodyClassName,
  placement = "center",
  children,
}: DialogProps) => {
  const [internalOpen, setInternalOpen] = React.useState(open ?? false);
  const titleId = React.useId();
  const descriptionId = React.useId();
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);
  const isLegacyDialog =
    title !== undefined ||
    description !== undefined ||
    footer !== undefined ||
    maxWidthClassName !== undefined ||
    contentClassName !== undefined ||
    bodyClassName !== undefined;

  React.useEffect(() => {
    if (open !== undefined) setInternalOpen(open);
  }, [open]);

  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  // Keep the focus/keyboard effect stable while a parent renders edited fields.
  const setOpen = React.useCallback((next: boolean) => {
    setInternalOpen(next);
    onOpenChangeRef.current?.(next);
  }, []);

  const resolvedOpen = open ?? internalOpen;

  React.useEffect(() => {
    if (!isLegacyDialog || !resolvedOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      (getFocusableElements(panel)[0] ?? panel).focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = getFocusableElements(panelRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, [isLegacyDialog, resolvedOpen, setOpen]);

  if (isLegacyDialog) {
    if (!resolvedOpen) return null;

    return createPortal(
      <div
        className={cn(
          "fixed inset-0 z-[100] flex justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4",
          placement === "bottom" ? "items-end sm:items-center" : "items-center",
        )}
        onClick={() => setOpen(false)}
      >
        <div
          ref={panelRef}
          className={cn(
            "flex w-full max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-lg border border-border bg-surface text-foreground shadow-lg animate-in fade-in zoom-in-95 duration-200 outline-none",
            maxWidthClassName || "max-w-lg",
            contentClassName,
          )}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descriptionId : undefined}
        >
          {title || description ? (
            <div className="border-b px-4 py-3 sm:px-6">
              {title ? (
                <h2 id={titleId} className="text-lg font-semibold">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p id={descriptionId} className="text-sm text-muted-foreground">
                  {description}
                </p>
              ) : null}
            </div>
          ) : null}

          <div
            className={cn(
              "flex-1 overflow-y-auto px-4 py-4 sm:px-6",
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer ? (
            <div className="border-t bg-background/95 px-4 py-3 sm:px-6">
              {footer}
            </div>
          ) : null}
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <DialogContext.Provider value={{ open: resolvedOpen, setOpen }}>
      {children}
    </DialogContext.Provider>
  );
};

const useDialog = () => {
  const ctx = React.useContext(DialogContext);
  if (!ctx)
    throw new Error(
      translateUi(
        "dialogComponentsMustBeUsedWithin",
        "Dialog components must be used within <Dialog>",
      ),
    );
  return ctx;
};

type DialogTriggerProps = { asChild?: boolean; children: React.ReactElement };

export const DialogTrigger = ({ asChild, children }: DialogTriggerProps) => {
  const { setOpen } = useDialog();
  const handleClick = (event: React.MouseEvent) => {
    children.props?.onClick?.(event);
    if (!event.defaultPrevented) setOpen(true);
  };

  return asChild && React.isValidElement(children) ? (
    React.cloneElement(children, { onClick: handleClick } as any)
  ) : (
    <button type="button" onClick={handleClick}>
      {children}
    </button>
  );
};

type DialogContentProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogContent = ({
  className,
  children,
  ...rest
}: DialogContentProps) => {
  const { open, setOpen } = useDialog();
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md animate-in fade-in duration-300" />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "relative w-full max-w-xl max-h-[calc(100vh-4rem)] overflow-y-auto rounded-lg border border-border bg-surface p-8 shadow-2xl animate-in zoom-in-95 fade-in duration-300",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

export const DialogTitle = React.forwardRef<
  React.ElementRef<"h2">,
  React.ComponentPropsWithoutRef<"h2">
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  >
    {props.children}
  </h2>
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = React.forwardRef<
  React.ElementRef<"p">,
  React.ComponentPropsWithoutRef<"p">
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";
